
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Plus, Target, Edit, Trash2, Save, X } from 'lucide-react';

// Pool table diagram component
const PoolTableDiagram = ({ diagram, onDiagramChange }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [balls, setBalls] = useState(diagram?.balls || []);
    const [selectedBall, setSelectedBall] = useState(null);

    const tableWidth = 300;
    const tableHeight = 150;
    const ballRadius = 8;

    const ballColors = {
        1: '#FFFF00', // Yellow
        2: '#0000FF', // Blue
        3: '#FF0000', // Red
        4: '#800080', // Purple
        5: '#FFA500', // Orange
        6: '#008000', // Green
        7: '#8B0000', // Dark Red
        8: '#000000', // Black
        9: '#FFFF00', // Yellow (striped)
        10: '#0000FF', // Blue (striped)
        'cue': '#FFFFFF', // White
    };

    const handleTableClick = (e) => {
        if (!isEditing) return;
        
        const svg = e.currentTarget;
        const rect = svg.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        // Check if clicking on existing ball
        const clickedBall = balls.find(ball => {
            const distance = Math.sqrt((ball.x - x) ** 2 + (ball.y - y) ** 2);
            return distance < ballRadius * 2;
        });

        if (clickedBall) {
            setSelectedBall(clickedBall);
        } else if (selectedBall) {
            // Add new ball
            const newBall = {
                id: Date.now(),
                number: selectedBall.number,
                x: x,
                y: y
            };
            setBalls(prev => [...prev, newBall]);
            setSelectedBall(null);
        }
    };

    const removeBall = (ballId) => {
        setBalls(prev => prev.filter(ball => ball.id !== ballId));
    };

    const saveDiagram = () => {
        onDiagramChange({ balls });
        setIsEditing(false);
    };

    const cancelEdit = () => {
        setBalls(diagram?.balls || []);
        setIsEditing(false);
        setSelectedBall(null);
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <label className="block text-sm font-medium text-slate-300">
                    Drill Diagram
                </label>
                {!isEditing ? (
                    <button
                        type="button"
                        onClick={() => setIsEditing(true)}
                        className="flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
                    >
                        <Edit className="w-4 h-4" />
                        Edit
                    </button>
                ) : (
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={saveDiagram}
                            className="flex items-center gap-2 px-3 py-1 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition-colors"
                        >
                            <Save className="w-4 h-4" />
                            Save
                        </button>
                        <button
                            type="button"
                            onClick={cancelEdit}
                            className="flex items-center gap-2 px-3 py-1 bg-gray-600 text-white rounded-lg text-sm hover:bg-gray-700 transition-colors"
                        >
                            <X className="w-4 h-4" />
                            Cancel
                        </button>
                    </div>
                )}
            </div>

            {/* Pool Table */}
            <div className="relative">
                <svg
                    width={tableWidth}
                    height={tableHeight}
                    className="border-2 border-slate-600 rounded-lg bg-green-800"
                    onClick={handleTableClick}
                >
                    {/* Table felt */}
                    <rect width="100%" height="100%" fill="#0F5132" />
                    
                    {/* Table border */}
                    <rect x="2" y="2" width={tableWidth - 4} height={tableHeight - 4} fill="none" stroke="#8B4513" strokeWidth="4" />
                    
                    {/* Pockets */}
                    <circle cx="10" cy="10" r="8" fill="#000" />
                    <circle cx={tableWidth / 2} cy="10" r="8" fill="#000" />
                    <circle cx={tableWidth - 10} cy="10" r="8" fill="#000" />
                    <circle cx="10" cy={tableHeight - 10} r="8" fill="#000" />
                    <circle cx={tableWidth / 2} cy={tableHeight - 10} r="8" fill="#000" />
                    <circle cx={tableWidth - 10} cy={tableHeight - 10} r="8" fill="#000" />
                    
                    {/* Balls */}
                    {balls.map(ball => (
                        <g key={ball.id}>
                            <circle
                                cx={ball.x}
                                cy={ball.y}
                                r={ballRadius}
                                fill={ballColors[ball.number] || '#FFFFFF'}
                                stroke="#000"
                                strokeWidth="1"
                            />
                            {ball.number !== 'cue' && (
                                <text
                                    x={ball.x}
                                    y={ball.y + 2}
                                    textAnchor="middle"
                                    fontSize="10"
                                    fill="#000"
                                    fontWeight="bold"
                                >
                                    {ball.number}
                                </text>
                            )}
                            {isEditing && (
                                <circle
                                    cx={ball.x}
                                    cy={ball.y}
                                    r={ballRadius + 2}
                                    fill="none"
                                    stroke="#FF0000"
                                    strokeWidth="2"
                                    className="cursor-pointer"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removeBall(ball.id);
                                    }}
                                />
                            )}
                        </g>
                    ))}
                </svg>
            </div>

            {/* Ball Selector */}
            {isEditing && (
                <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-300">
                        Select Ball to Place:
                    </label>
                    <div className="flex flex-wrap gap-2">
                        {['cue', 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(ballNum => (
                            <button
                                key={ballNum}
                                type="button"
                                onClick={() => setSelectedBall({ number: ballNum })}
                                className={`w-8 h-8 rounded-full border-2 transition-all ${
                                    selectedBall?.number === ballNum
                                        ? 'border-white ring-2 ring-blue-400'
                                        : 'border-slate-600'
                                }`}
                                style={{ backgroundColor: ballColors[ballNum] }}
                            >
                                {ballNum !== 'cue' && (
                                    <span className="text-xs font-bold text-black">
                                        {ballNum}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                    {selectedBall && (
                        <p className="text-sm text-slate-400">
                            Click on the table to place the {selectedBall.number === 'cue' ? 'cue ball' : `#${selectedBall.number} ball`}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

const DrillCard = ({ drill, onEdit, onDelete }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-slate-700/50 p-4 rounded-xl border border-slate-600"
    >
        <div className="flex justify-between items-start mb-3">
            <div>
                <h3 className="font-bold text-lg text-white">{drill.name}</h3>
                <p className="text-sm text-slate-400">{drill.description}</p>
            </div>
            <div className="flex gap-2">
                <button
                    onClick={() => onEdit(drill)}
                    className="p-1.5 rounded-lg bg-slate-600 hover:bg-slate-500 transition-colors"
                >
                    <Edit className="w-4 h-4 text-slate-300" />
                </button>
                <button
                    onClick={() => onDelete(drill.id)}
                    className="p-1.5 rounded-lg bg-red-600 hover:bg-red-500 transition-colors"
                >
                    <Trash2 className="w-4 h-4 text-white" />
                </button>
            </div>
        </div>
        
        {/* Diagram Display */}
        {drill.diagram && drill.diagram.balls && drill.diagram.balls.length > 0 && (
            <div className="mb-3">
                <div className="text-sm font-semibold text-slate-300 mb-2">Drill Setup</div>
                <div className="bg-slate-800/50 p-2 rounded-lg">
                    <svg width="200" height="100" className="w-full">
                        <rect width="100%" height="100%" fill="#0F5132" />
                        <rect x="2" y="2" width="196" height="96" fill="none" stroke="#8B4513" strokeWidth="2" />
                        
                        {/* Pockets */}
                        <circle cx="8" cy="8" r="4" fill="#000" />
                        <circle cx="100" cy="8" r="4" fill="#000" />
                        <circle cx="192" cy="8" r="4" fill="#000" />
                        <circle cx="8" cy="92" r="4" fill="#000" />
                        <circle cx="100" cy="92" r="4" fill="#000" />
                        <circle cx="192" cy="92" r="4" fill="#000" />
                        
                        {/* Balls */}
                        {drill.diagram.balls.map(ball => {
                            const scaleX = 200 / 300;
                            const scaleY = 100 / 150;
                            const x = ball.x * scaleX;
                            const y = ball.y * scaleY;
                            
                            const ballColors = {
                                1: '#FFFF00', 2: '#0000FF', 3: '#FF0000', 4: '#800080',
                                5: '#FFA500', 6: '#008000', 7: '#8B0000', 8: '#000000',
                                9: '#FFFF00', 10: '#0000FF', 'cue': '#FFFFFF'
                            };
                            
                            return (
                                <g key={ball.id}>
                                    <circle
                                        cx={x}
                                        cy={y}
                                        r={4}
                                        fill={ballColors[ball.number] || '#FFFFFF'}
                                        stroke="#000"
                                        strokeWidth="0.5"
                                    />
                                    {ball.number !== 'cue' && (
                                        <text
                                            x={x}
                                            y={y + 1}
                                            textAnchor="middle"
                                            fontSize="6"
                                            fill="#000"
                                            fontWeight="bold"
                                        >
                                            {ball.number}
                                        </text>
                                    )}
                                </g>
                            );
                        })}
                    </svg>
                </div>
            </div>
        )}
        
        <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="bg-slate-600/50 p-2 rounded-lg">
                <div className="text-xs text-slate-400">Best Score</div>
                <div className="font-bold text-white">{drill.bestScore || 'N/A'}</div>
            </div>
            <div className="bg-slate-600/50 p-2 rounded-lg">
                <div className="text-xs text-slate-400">Total Sessions</div>
                <div className="font-bold text-white">{drill.sessions?.length || 0}</div>
            </div>
        </div>

        {drill.sessions && drill.sessions.length > 0 && (
            <div className="space-y-2">
                <div className="text-sm font-semibold text-slate-300">Recent Sessions</div>
                <div className="max-h-32 overflow-y-auto space-y-1">
                    {drill.sessions.slice(-3).reverse().map((session, index) => (
                        <div key={index} className="flex justify-between items-center text-sm bg-slate-600/30 p-2 rounded">
                            <span className="text-slate-300">{session.score}</span>
                            <span className="text-slate-400 text-xs">
                                {new Date(session.date).toLocaleDateString()}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        )}
    </motion.div>
);

const DrillForm = ({ drill, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        name: drill?.name || '',
        description: drill?.description || '',
        targetScore: drill?.targetScore || '',
        category: drill?.category || 'positioning',
        diagram: drill?.diagram || null
    });

    const categories = [
        { value: 'positioning', label: 'Positioning' },
        { value: 'potting', label: 'Potting' },
        { value: 'safety', label: 'Safety' },
        { value: 'break', label: 'Break' },
        { value: 'pattern', label: 'Pattern Play' },
        { value: 'pressure', label: 'Pressure Situations' }
    ];

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    const handleDiagramChange = (diagram) => {
        setFormData(prev => ({ ...prev, diagram }));
    };

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-slate-800/50 p-6 rounded-xl border border-slate-700"
        >
            <h3 className="text-xl font-bold mb-4">
                {drill ? 'Edit Drill' : 'Add New Drill'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium mb-2 text-slate-300">
                        Drill Name
                    </label>
                    <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-500"
                        placeholder="e.g., Line Drill, Cross Corner, etc."
                        required
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium mb-2 text-slate-300">
                        Description
                    </label>
                    <textarea
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-500"
                        placeholder="Describe the drill setup and objectives..."
                        rows={3}
                        required
                    />
                </div>

                {/* Diagram Component */}
                <PoolTableDiagram 
                    diagram={formData.diagram} 
                    onDiagramChange={handleDiagramChange} 
                />

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-2 text-slate-300">
                            Category
                        </label>
                        <select
                            value={formData.category}
                            onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-500"
                        >
                            {categories.map(category => (
                                <option key={category.value} value={category.value}>
                                    {category.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-2 text-slate-300">
                            Target Score
                        </label>
                        <input
                            type="number"
                            value={formData.targetScore}
                            onChange={(e) => setFormData(prev => ({ ...prev, targetScore: e.target.value }))}
                            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-500"
                            placeholder="Optional"
                        />
                    </div>
                </div>

                <div className="flex gap-3 pt-4">
                    <button
                        type="submit"
                        className="flex-1 bg-blue-600 text-white py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                    >
                        <Save className="w-4 h-4" />
                        {drill ? 'Update Drill' : 'Add Drill'}
                    </button>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="flex-1 bg-slate-600 text-white py-2 rounded-lg font-medium hover:bg-slate-500 transition-colors flex items-center justify-center gap-2"
                    >
                        <X className="w-4 h-4" />
                        Cancel
                    </button>
                </div>
            </form>
        </motion.div>
    );
};

const SessionForm = ({ drill, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        score: '',
        notes: '',
        duration: ''
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave({
            ...formData,
            date: new Date().toISOString(),
            score: parseInt(formData.score),
            duration: parseInt(formData.duration) || 0
        });
    };

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-slate-800/50 p-6 rounded-xl border border-slate-700"
        >
            <h3 className="text-xl font-bold mb-4">
                Log Session - {drill.name}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-2 text-slate-300">
                            Score
                        </label>
                        <input
                            type="number"
                            value={formData.score}
                            onChange={(e) => setFormData(prev => ({ ...prev, score: e.target.value }))}
                            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-500"
                            placeholder="0"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-2 text-slate-300">
                            Duration (minutes)
                        </label>
                        <input
                            type="number"
                            value={formData.duration}
                            onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-500"
                            placeholder="Optional"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium mb-2 text-slate-300">
                        Notes
                    </label>
                    <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-500"
                        placeholder="How did the session go? Any observations?"
                        rows={3}
                    />
                </div>

                <div className="flex gap-3 pt-4">
                    <button
                        type="submit"
                        className="flex-1 bg-green-600 text-white py-2 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                    >
                        <Save className="w-4 h-4" />
                        Log Session
                    </button>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="flex-1 bg-slate-600 text-white py-2 rounded-lg font-medium hover:bg-slate-500 transition-colors flex items-center justify-center gap-2"
                    >
                        <X className="w-4 h-4" />
                        Cancel
                    </button>
                </div>
            </form>
        </motion.div>
    );
};

export const DrillView = ({ currentUserData, onBack }) => {
    const [drills, setDrills] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [showSessionForm, setShowSessionForm] = useState(false);
    const [editingDrill, setEditingDrill] = useState(null);
    const [selectedDrill, setSelectedDrill] = useState(null);
    const [loading, setLoading] = useState(true);

    const loadDrills = useCallback(async () => {
        // TODO: Implement actual Firestore loading
        // For now, using localStorage
        const savedDrills = localStorage.getItem(`drills_${currentUserData.uid}`);
        if (savedDrills) {
            setDrills(JSON.parse(savedDrills));
        }
        setLoading(false);
    }, [currentUserData.uid]);

    useEffect(() => {
        loadDrills();
    }, [loadDrills]);

    const saveDrills = async (newDrills) => {
        // TODO: Implement actual Firestore saving
        // For now, using localStorage
        localStorage.setItem(`drills_${currentUserData.uid}`, JSON.stringify(newDrills));
        setDrills(newDrills);
    };

    const handleAddDrill = () => {
        setEditingDrill(null);
        setShowForm(true);
    };

    const handleEditDrill = (drill) => {
        setEditingDrill(drill);
        setShowForm(true);
    };

    const handleSaveDrill = (formData) => {
        if (editingDrill) {
            const updatedDrills = drills.map(drill => 
                drill.id === editingDrill.id 
                    ? { ...drill, ...formData }
                    : drill
            );
            saveDrills(updatedDrills);
        } else {
            const newDrill = {
                id: Date.now().toString(),
                ...formData,
                sessions: [],
                createdAt: new Date().toISOString()
            };
            saveDrills([...drills, newDrill]);
        }
        setShowForm(false);
        setEditingDrill(null);
    };

    const handleDeleteDrill = (drillId) => {
        const updatedDrills = drills.filter(drill => drill.id !== drillId);
        saveDrills(updatedDrills);
    };

    const handleLogSession = (drill) => {
        setSelectedDrill(drill);
        setShowSessionForm(true);
    };

    const handleSaveSession = (sessionData) => {
        const updatedDrills = drills.map(drill => {
            if (drill.id === selectedDrill.id) {
                const sessions = [...(drill.sessions || []), sessionData];
                const bestScore = Math.max(...sessions.map(s => s.score));
                return {
                    ...drill,
                    sessions,
                    bestScore
                };
            }
            return drill;
        });
        saveDrills(updatedDrills);
        setShowSessionForm(false);
        setSelectedDrill(null);
    };

    if (loading) {
        return (
            <div className="p-4 flex justify-center items-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="p-2 sm:p-4 max-w-4xl mx-auto pb-8">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                    <button onClick={onBack} className="p-2 rounded-full hover:bg-slate-700 -ml-2">
                        <ArrowLeft className="w-6 h-6" />
                    </button>
                    <h1 className="text-2xl sm:text-3xl font-bold ml-4">Drill Tracker</h1>
                </div>
                <button
                    onClick={handleAddDrill}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" />
                    Add Drill
                </button>
            </div>

            {showForm && (
                <div className="mb-6">
                    <DrillForm
                        drill={editingDrill}
                        onSave={handleSaveDrill}
                        onCancel={() => {
                            setShowForm(false);
                            setEditingDrill(null);
                        }}
                    />
                </div>
            )}

            {showSessionForm && selectedDrill && (
                <div className="mb-6">
                    <SessionForm
                        drill={selectedDrill}
                        onSave={handleSaveSession}
                        onCancel={() => {
                            setShowSessionForm(false);
                            setSelectedDrill(null);
                        }}
                    />
                </div>
            )}

            {drills.length === 0 ? (
                <div className="text-center bg-slate-800/50 p-8 rounded-xl border border-slate-700">
                    <Target className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-white mb-2">No Drills Yet</h3>
                    <p className="text-slate-400 mb-4">
                        Create your first drill to start tracking your practice progress.
                    </p>
                    <button
                        onClick={handleAddDrill}
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                    >
                        Create Your First Drill
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {drills.map(drill => (
                        <div key={drill.id} className="relative">
                            <DrillCard
                                drill={drill}
                                onEdit={handleEditDrill}
                                onDelete={handleDeleteDrill}
                            />
                            <button
                                onClick={() => handleLogSession(drill)}
                                className="absolute bottom-4 right-4 bg-green-600 text-white p-2 rounded-lg hover:bg-green-700 transition-colors"
                                title="Log Session"
                            >
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

